import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Input,
  Label,
  FormGroup,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import UserInfoModal from "../../components/Common/UserInfoModal";
import { getTokenStatusList, addTokensToZeroUsers } from "./helper/tokenStatusApi";

const TokenStatusPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userInfoModal, setUserInfoModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [filterApplied, setFilterApplied] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [count, setCount] = useState(0);
  const [addTokensModal, setAddTokensModal] = useState(false);
  const [tokenAmount, setTokenAmount] = useState("");
  const [addTokensLoading, setAddTokensLoading] = useState(false);

  // Filter states
  const [tokenCount, setTokenCount] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);
  const [totalRows, setTotalRows] = useState(0);

  // Refs for optimization
  const tokenCountTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const isLoadingRef = useRef(false);
  const hasInitialFetchedRef = useRef(false);

  // Debounced token count
  const [debouncedTokenCount, setDebouncedTokenCount] = useState("");

  // Fetch list function
  const fetchList = useCallback(async () => {
    if (isLoadingRef.current) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    isLoadingRef.current = true;
    setLoading(true);

    try {
      const filters = {
        limit,
        page: currentPage,
        token_count: debouncedTokenCount !== "" ? parseInt(debouncedTokenCount) : undefined,
      };

      const result = await getTokenStatusList(filters);

      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      // Transform user_ids array into objects for the table
      const tableData = result.data.map((userId) => ({ userId }));
      
      setData(tableData);
      setTotalRows(result.pagination.totalRecords);
      setFilterApplied(result.filter_applied || "");
      setTotalCount(result.total_count || 0);
      setCount(result.count || 0);
    } catch (error) {
      if (error.name !== "AbortError" && isMountedRef.current) {
        console.error("Error fetching token status list:", error);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
        isLoadingRef.current = false;
      }
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [debouncedTokenCount, currentPage, limit]);

  // Handlers
  const handleViewUserInfo = useCallback((userId) => {
    if (!userId) return;
    setSelectedUserId(userId);
    setUserInfoModal(true);
  }, []);

  const handleAddTokensToZeroUsers = useCallback(async () => {
    if (!tokenAmount || isNaN(tokenAmount) || parseInt(tokenAmount) <= 0) {
      alert("Please enter a valid positive number for tokens");
      return;
    }

    setAddTokensLoading(true);
    try {
      await addTokensToZeroUsers({
        tokens: parseInt(tokenAmount),
      });

      // Refresh the list
      setAddTokensModal(false);
      setTokenAmount("");
      if (hasInitialFetchedRef.current) {
        fetchList();
      }
    } catch (error) {
      console.error("Error adding tokens:", error);
      alert(error.response?.data?.message || error.message || "Failed to add tokens");
    } finally {
      setAddTokensLoading(false);
    }
  }, [tokenAmount, fetchList]);

  const columns = useMemo(
    () => [
      {
        name: <span className="font-weight-bold fs-13">User ID</span>,
        selector: (row) => row.userId,
        sortable: true,
        cell: (row) => (
          <div 
            className="font-monospace small text-primary cursor-pointer"
            style={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={() => handleViewUserInfo(row.userId)}
            title="Click to view user details"
          >
            {row.userId || "-"}
          </div>
        ),
      },
    ],
    [handleViewUserInfo]
  );

  // Initial fetch
  useEffect(() => {
    isMountedRef.current = true;
    if (!hasInitialFetchedRef.current) {
      fetchList();
      hasInitialFetchedRef.current = true;
    }

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (tokenCountTimeoutRef.current) {
        clearTimeout(tokenCountTimeoutRef.current);
      }
    };
  }, []);

  // Debounce token count search
  useEffect(() => {
    if (tokenCountTimeoutRef.current) {
      clearTimeout(tokenCountTimeoutRef.current);
    }

    tokenCountTimeoutRef.current = setTimeout(() => {
      setDebouncedTokenCount(tokenCount);
    }, 1000);

    return () => {
      if (tokenCountTimeoutRef.current) {
        clearTimeout(tokenCountTimeoutRef.current);
      }
    };
  }, [tokenCount]);

  // Fetch when debounced token count changes
  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      setCurrentPage(1);
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTokenCount]);

  // Fetch when page changes
  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleFilterChange = (value) => {
    setTokenCount(value);
    setCurrentPage(1);
  };

  const clearFilters = useCallback(() => {
    setTokenCount("");
    setDebouncedTokenCount("");
    setCurrentPage(1);
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Token Status" breadcrumbItem="Token Status" />
          <Row>
            <Col xs={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    <i className="mdi mdi-token me-2"></i>Token Status
                  </h4>
                </CardHeader>
                <CardBody>
                  {/* Filters */}
                  <Row className="mb-3">
                    <Col md={4}>
                      <FormGroup>
                        <Label>Token Count (Greater than or equal to)</Label>
                        <Input
                          type="number"
                          min="0"
                          placeholder="Enter token count (e.g., 1)"
                          value={tokenCount}
                          onChange={(e) => handleFilterChange(e.target.value)}
                        />
                        <small className="text-muted">
                          Filter users with tokens >= this value. Leave empty to show users with zero tokens or not in user_broadcast_tokens.
                        </small>
                      </FormGroup>
                    </Col>
                    <Col md={8}>
                      <div className="d-flex align-items-end h-100 flex-wrap gap-2">
                        {(!debouncedTokenCount || debouncedTokenCount === "") && (
                          <Button 
                            color="primary" 
                            onClick={() => setAddTokensModal(true)}
                            className="me-2"
                          >
                            <i className="mdi mdi-token me-1"></i>Add Tokens to All Zero-Token Users
                          </Button>
                        )}
                        <Button color="secondary" onClick={clearFilters} className="me-2">
                          <i className="mdi mdi-filter-remove me-1"></i>Clear Filter
                        </Button>
                        {filterApplied && (
                          <Badge color="info" className="px-3 py-2">
                            <i className="mdi mdi-filter me-1"></i>
                            {filterApplied}
                          </Badge>
                        )}
                        {totalCount > 0 && (
                          <Badge color="success" className="px-3 py-2">
                            Total: {totalCount} | Showing: {count}
                          </Badge>
                        )}
                      </div>
                    </Col>
                  </Row>

                  {/* Data Table */}
                  <DataTable
                    columns={columns}
                    data={data}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationDefaultPage={currentPage}
                    onChangePage={(page) => setCurrentPage(page)}
                    paginationPerPage={limit}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    noDataComponent={
                      <div className="text-center py-4">
                        <i className="mdi mdi-account-off" style={{ fontSize: "3rem", color: "#6c757d" }}></i>
                        <p className="mt-3 text-muted">No users found</p>
                      </div>
                    }
                    highlightOnHover
                    pointerOnHover
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <UserInfoModal
        isOpen={userInfoModal}
        toggle={() => {
          setUserInfoModal(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
        onUpdate={() => {
          // Optionally refresh the list after user action
          if (hasInitialFetchedRef.current) {
            fetchList();
          }
        }}
      />

      {/* Add Tokens to Zero Users Modal */}
      <Modal isOpen={addTokensModal} toggle={() => setAddTokensModal(false)} centered>
        <ModalHeader toggle={() => setAddTokensModal(false)} className="bg-primary text-white">
          <i className="mdi mdi-token me-2"></i>Add Tokens to All Zero-Token Users
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <p className="mb-0">Add tokens to all users who have zero tokens:</p>
            {totalCount > 0 && (
              <small className="text-muted">
                This will affect <strong>{totalCount}</strong> user{totalCount !== 1 ? 's' : ''} with zero tokens.
              </small>
            )}
          </div>
          <FormGroup>
            <Label>Token Amount <span className="text-danger">*</span></Label>
            <Input
              type="number"
              min="1"
              step="1"
              value={tokenAmount}
              onChange={(e) => setTokenAmount(e.target.value)}
              placeholder="Enter number of tokens to add"
              required
            />
            <small className="text-muted">Enter a positive number of tokens to add to all users with zero tokens.</small>
          </FormGroup>
          {tokenAmount && !isNaN(tokenAmount) && parseInt(tokenAmount) > 0 && (
            <div className="alert alert-warning mb-0">
              <i className="mdi mdi-alert-circle me-2"></i>
              <strong>Confirm:</strong> You are about to add <strong>{parseInt(tokenAmount)}</strong> token{parseInt(tokenAmount) !== 1 ? 's' : ''} to <strong>{totalCount}</strong> user{totalCount !== 1 ? 's' : ''} with zero tokens.
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => {
            setAddTokensModal(false);
            setTokenAmount("");
          }} disabled={addTokensLoading}>
            Cancel
          </Button>
          <Button 
            color="primary" 
            onClick={handleAddTokensToZeroUsers} 
            disabled={addTokensLoading || !tokenAmount || isNaN(tokenAmount) || parseInt(tokenAmount) <= 0}
          >
            {addTokensLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Adding Tokens...
              </>
            ) : (
              <>
                <i className="mdi mdi-token me-2"></i>Add Tokens
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default TokenStatusPage;

